// frontend/src/services/eventAdminService.js
// Event Admin assignments: which events an `event_admin` user may manage.
// Writes are gated by RLS (only super_admin can insert/delete assignment rows);
// an event_admin can read their own rows (eaa_self_read policy).
import { supabase } from './supabaseCore';

export const eventAdminService = {
    /**
     * Event IDs currently assigned to a given user. Super-admin view
     * (used by the assignment modal in Settings).
     */
    listAssignments: async (userId) => {
        const { data, error } = await supabase
            .from('event_admin_assignments')
            .select('event_id')
            .eq('user_id', userId);
        if (error) throw error;
        return (data || []).map(r => r.event_id);
    },

    /**
     * Reconcile a user's assignments to exactly `eventIds` (add missing,
     * remove extras). Super-admin only; RLS enforces this server-side.
     */
    setAssignments: async (userId, eventIds) => {
        const desired = [...new Set((eventIds || []).filter(Boolean))];

        const { data: current, error: readErr } = await supabase
            .from('event_admin_assignments')
            .select('event_id')
            .eq('user_id', userId);
        if (readErr) throw readErr;

        const currentIds = (current || []).map(r => r.event_id);
        const toAdd = desired.filter(id => !currentIds.includes(id));
        const toRemove = currentIds.filter(id => !desired.includes(id));

        if (toRemove.length) {
            const { error } = await supabase
                .from('event_admin_assignments')
                .delete()
                .eq('user_id', userId)
                .in('event_id', toRemove);
            if (error) throw error;
        }
        if (toAdd.length) {
            const { error } = await supabase
                .from('event_admin_assignments')
                .insert(toAdd.map(event_id => ({ user_id: userId, event_id })));
            if (error) throw error;
        }
    },

    /**
     * The logged-in event_admin's own assigned events, joined to `events`
     * for code/name. RLS scopes the assignment rows to the caller.
     */
    getMyAssignedEvents: async () => {
        const { data, error } = await supabase
            .from('event_admin_assignments')
            .select('event_id, events(id, code, short_name, name, is_archived)');
        if (error) throw error;
        return (data || []).map(r => r.events).filter(Boolean);
    },
};
